/**
 * Usage reporting contract.
 *
 * Usage is Amoreg's own: every model call the platform makes is recorded as an
 * AI-gateway generation against the organization that paid for it, and this
 * contract reports those generations back as pre-aggregated `(day, tier,
 * model)` buckets. Individual generations never cross the wire, and nothing is
 * ever read outside the acting organization.
 *
 * Costs are reported in **pounds**: Amoreg bills in GBP, so the page must too.
 * The gateway prices in USD, so every environment converts with the rate it
 * reports in {@link UsageExchangeRate} — quoted alongside the figures rather
 * than baked in silently.
 *
 * @module usage
 */
import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Bumped whenever the shape of {@link UsageSummary} changes incompatibly. The
 * client renders partial coverage when an environment reports an older version
 * rather than failing the whole page.
 */
export const USAGE_CONTRACT_VERSION = 4 as const;

/**
 * The Amoreg tier a generation was served on.
 *
 * Deliberately the product's own tier rather than the model vendor behind it.
 * Which model each tier resolves to is an implementation detail that changes
 * — `chat-model` and `chat-model-reasoning` are both Anthropic models today
 * and were both OpenAI ones a month ago — so a page split by vendor answers a
 * question nobody asked and changes meaning under the reader's feet.
 *
 * `amoreg` and `amoregPro` are the composer's two models; `amoregFlash` is
 * any turn on the Ultrafast service tier (and the retired Ultrafast model),
 * see `@repo/shared/agentModels`.
 */
/** Which transcript format a usage record was read from. */
export const UsageProviderKind = Schema.Literals(["claude", "codex"]);
export type UsageProviderKind = typeof UsageProviderKind.Type;

export const UsageTier = Schema.Literals(["amoregFlash", "amoreg", "amoregPro"]);
export type UsageTier = typeof UsageTier.Type;

/**
 * A calendar day in the reporting time zone, formatted `YYYY-MM-DD`.
 *
 * Days are bucketed server-side so that a turn always lands on the day the user
 * experienced it, not the UTC day.
 */
const USAGE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const UsageDay = TrimmedNonEmptyString.check(Schema.isPattern(USAGE_DAY_PATTERN)).pipe(
  Schema.brand("UsageDay"),
);
export type UsageDay = typeof UsageDay.Type;

/**
 * Why a bucket's cost is what it is.
 *
 * - `gatewayBilled` - the gateway billed an explicit amount for the call.
 * - `modelPriced` - no billed amount, so the model was matched against the
 *   LiteLLM rate table.
 * - `unpriced` - tokens are known, rates are not. Counted in totals, excluded
 *   from cost.
 */
export const UsageCostSource = Schema.Literals(["gatewayBilled", "modelPriced", "unpriced"]);
export type UsageCostSource = typeof UsageCostSource.Type;

/**
 * Token counts for a bucket.
 *
 * `cachedInputTokens` and `cacheCreationTokens` are disjoint from
 * `uncachedInputTokens`; summing all three gives total input. `reasoningTokens`
 * is a *subset* of `outputTokens` (the gateway reports it that way, and
 * Anthropic folds thinking into output), so it must never be added on top.
 */
export const UsageTokenTotals = Schema.Struct({
  uncachedInputTokens: NonNegativeInt,
  cachedInputTokens: NonNegativeInt,
  cacheCreationTokens: NonNegativeInt,
  outputTokens: NonNegativeInt,
  reasoningTokens: NonNegativeInt,
});
export type UsageTokenTotals = typeof UsageTokenTotals.Type;

/**
 * One `(day, tier, model, agent)` cell.
 *
 * `costGbp` is what the organization was billed for these tokens, converted at
 * the summary's exchange rate. `unpricedRecords` counts generations whose
 * tokens are included in the token totals but which contributed nothing to
 * `costGbp`.
 */
export const UsageBucket = Schema.Struct({
  day: UsageDay,
  tier: UsageTier,
  model: TrimmedNonEmptyString,
  /**
   * The delegated helper that made these calls (`browser`, `worker`,
   * `insight`); absent for the lead agent's own calls and everything outside
   * the workspace.
   */
  agent: Schema.optional(TrimmedNonEmptyString),
  totals: UsageTokenTotals,
  costGbp: Schema.Number,
  /**
   * What the cached input would have cost at full input rates minus what it
   * actually cost. Requires the rate table, so it is computed alongside cost
   * rather than derived on the client.
   */
  cacheSavingsGbp: Schema.Number,
  costSource: UsageCostSource,
  /** Distinct model calls (gateway generations) in this cell. */
  records: NonNegativeInt,
  unpricedRecords: NonNegativeInt,
  /** Distinct conversations that contributed to this cell. */
  sessions: NonNegativeInt,
});
export type UsageBucket = typeof UsageBucket.Type;

/**
 * Identifies the usage ledger a source read from.
 *
 * Several environments can be pointed at one Amoreg database — a worktree
 * server beside the primary, or two developers on the same dev stack — and
 * would otherwise report the same organization's generations twice. The client
 * drops duplicate fingerprints before merging.
 */
export const UsageSourceFingerprint = Schema.Struct({
  /** `host:port/database` of the Amoreg Postgres the summary was read from. */
  databaseId: TrimmedNonEmptyString,
  /**
   * The organization the generations were scoped to, or `*` when the session
   * reads every organization it belongs to. Part of the identity because two
   * environments on one database can be scoped to different tenants, and those
   * are genuinely different data rather than a duplicate.
   */
  organizationScope: TrimmedNonEmptyString,
});
export type UsageSourceFingerprint = typeof UsageSourceFingerprint.Type;

export const UsageSourceStatus = Schema.Literals(["ok", "missing", "partial", "failed"]);
export type UsageSourceStatus = typeof UsageSourceStatus.Type;

export const UsageSource = Schema.Struct({
  fingerprint: UsageSourceFingerprint,
  status: UsageSourceStatus,
  /** Generations read for the window. */
  scannedRecords: NonNegativeInt,
  /** Generations that carried no usable token counts and were skipped. */
  skippedRecords: NonNegativeInt,
  /**
   * Distinct conversations seen in the window. Buckets also carry per-bucket
   * session counts, but a conversation spans days and models, so summing those
   * overcounts; this is the figure clients should total.
   */
  distinctSessions: NonNegativeInt,
  message: Schema.NullOr(TrimmedNonEmptyString),
});
export type UsageSource = typeof UsageSource.Type;

export const UsagePricingStatus = Schema.Literals(["fresh", "cached", "unavailable"]);
export type UsagePricingStatus = typeof UsagePricingStatus.Type;

/**
 * Provenance for the rate table, so the UI can be honest about how good the
 * cost figures are.
 */
export const UsagePricing = Schema.Struct({
  status: UsagePricingStatus,
  source: TrimmedNonEmptyString,
  fetchedAt: Schema.NullOr(Schema.String),
  knownModels: NonNegativeInt,
});
export type UsagePricing = typeof UsagePricing.Type;

/**
 * The USD-to-GBP rate every figure in the summary was converted at.
 *
 * Quoted rather than assumed: a page that prints pounds without saying which
 * rate produced them cannot be reconciled against an invoice.
 */
export const UsageExchangeRate = Schema.Struct({
  status: UsagePricingStatus,
  source: TrimmedNonEmptyString,
  fetchedAt: Schema.NullOr(Schema.String),
  /** Pounds per dollar. Always positive. */
  usdToGbp: Schema.Number,
});
export type UsageExchangeRate = typeof UsageExchangeRate.Type;

/**
 * Whose usage the page is asking for.
 *
 * - `me` - the calls the signed-in person made, inside the organization named.
 * - `organization` - every member's calls in that organization.
 *
 * Like `organizationId`, it is a request: who "me" is comes from the session,
 * never from the payload.
 */
export const UsageSubject = Schema.Literals(["me", "organization"]);
export type UsageSubject = typeof UsageSubject.Type;

export const UsageSummaryInput = Schema.Struct({
  /** Inclusive first day of the window, in `timeZone`. */
  sinceDay: UsageDay,
  /** Inclusive last day of the window, in `timeZone`. */
  untilDay: UsageDay,
  /**
   * IANA zone the client wants days bucketed in. An offset would be wrong for
   * any window that crosses a DST boundary.
   */
  timeZone: TrimmedNonEmptyString,
  /**
   * The organization to report. Null asks for every organization the session
   * belongs to, which is what an org-less surface renders. Either way the
   * server re-checks membership: a client-supplied id is a request, never a
   * grant.
   */
  organizationId: Schema.NullOr(TrimmedNonEmptyString),
  subject: UsageSubject,
});
export type UsageSummaryInput = typeof UsageSummaryInput.Type;

export const UsageSummary = Schema.Struct({
  contractVersion: Schema.Number,
  readAt: Schema.String,
  timeZone: TrimmedNonEmptyString,
  sinceDay: UsageDay,
  untilDay: UsageDay,
  buckets: Schema.Array(UsageBucket),
  sources: Schema.Array(UsageSource),
  pricing: UsagePricing,
  exchangeRate: UsageExchangeRate,
  /** Wall-clock cost of the read, surfaced in diagnostics. */
  scanDurationMs: NonNegativeInt,
});
export type UsageSummary = typeof UsageSummary.Type;

export class UsageReadError extends Schema.TaggedErrorClass<UsageReadError>()("UsageReadError", {
  reason: Schema.Literals(["scanFailed", "invalidWindow", "forbidden"]),
  /** Stable, bounded description. The underlying failure travels in `cause`. */
  detail: TrimmedNonEmptyString,
  cause: Schema.optional(Schema.Defect()),
}) {
  override get message(): string {
    return `Usage read failed (${this.reason}): ${this.detail}`;
  }
}
